import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import GridViewIcon from '@mui/icons-material/GridView';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import appService from '../../../services/ApplicationService';
import HRService from '../../../services/HrService';
import DataTable from '../../common/DataGrid';
import { useParams } from 'react-router-dom';
import DynamicTabs from '../../common/DynamicTabs';
import Modal from '../../common/Modal';
import FileUploadUser from '../../common/FileUploadUser';
import DonutChart from '../../common/DonutChart';
import PersonAdd from '@mui/icons-material/PersonAdd';
import DownloadIcon from '@mui/icons-material/Download';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SFTPService from '../../../services/SFTPService';
import { format } from 'date-fns';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import Tooltip from '@mui/material/Tooltip';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SysOwnerSideBar from './SysOwnerSidebar';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileUploadUserBulk from '../../common/FileUploadUserBulk';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

// Custom styled components
const StatsCard = styled(mui.Paper)(({ theme, gradient }) => ({
  padding: '24px',
  borderRadius: '12px',
  height: '100%',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  background: gradient ? 
    `linear-gradient(45deg, ${theme.palette.primary.main} 10%, ${theme.palette.primary.light} 90%)` :
    '#fff',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.09)',
  },
}));

const ChartContainer = styled(mui.Paper)(({ theme }) => ({
  padding: '20px',
  borderRadius: '12px',
  height: '100%',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.09)',
  },
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
  fontWeight: 600,
  margin: '24px 0 16px 0',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: '8px',
  },
}));

const CategoryButton = styled(mui.Button)(({ theme, active }) => ({
  margin: theme.spacing(0, 1, 2, 0),
  borderRadius: '20px',
  padding: '8px 16px',
  textTransform: 'none',
  backgroundColor: active ? theme.palette.primary.light : 'transparent',
  color: active ? 'white' : theme.palette.text.primary,
  border: active ? 'none' : `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
  },
}));

const StatsValue = styled(mui.Typography)(({ theme, color }) => ({
  fontWeight: 700,
  fontSize: '2.2rem',
  color: color || theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const StatsLabel = styled(mui.Typography)(({ theme, color }) => ({
  fontSize: '0.875rem',
  color: color || theme.palette.text.secondary,
  fontWeight: 500,
}));

const DataCard = styled(mui.Card)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.09)',
  },
}));

const TrendBadge = styled(mui.Chip)(({ theme, trend }) => ({
  borderRadius: '16px',
  backgroundColor: trend === 'up' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
  color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main,
  fontWeight: 600,
  fontSize: '0.75rem',
}));

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes blink {
        0% { opacity: 0.6; }
        50% { opacity: 1; }
        100% { opacity: 0.6; }
    }
    .premium-card {
        position: relative;
        isolation: isolate;
        overflow: hidden;
    }
    .premium-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #42a5f5, #1976d2);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1;
    }
    .premium-card:hover::before {
        opacity: 1;
    }
    .stats-card {
        animation: fadeIn 0.5s ease-out;
    }
    .chart-container {
        animation: fadeIn 0.5s ease-out 0.1s both;
    }
    .data-section {
        animation: fadeIn 0.5s ease-out 0.2s both;
    }
`;

const MyApplicationManageUsers = () => {
    const [selectedapps, setSelectedApps] = useState([]);
    const { id } = useParams();
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openSetupModal, setOpenSetupModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [hrRecords, setHrRecords] = useState([]);
    const [lastRefresh, setLastRefresh] = useState([]);
    const [activeCategory, setActiveCategory] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedRoleFilter, setSelectedRoleFilter] = useState('All');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [selectedRolesForChart, setSelectedRolesForChart] = useState([]);
    const [showRoleSelector, setShowRoleSelector] = useState(false);
    const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [mappedUsers, setMappedUsers] = useState([]);
    const [unmappedUsers, setUnmappedUsers] = useState([]);
    const [hrMappingCategory, setHrMappingCategory] = useState('mapped');

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const handleOpenCreateModal = () => {
        setOpenCreateModal(true);
    };

    const handleOpenSetupModal = () => {
        setOpenSetupModal(true);
        setOpenCreateModal(false);
    };

    const handleCloseCreateModal = (event, reason) => {
        if (reason == 'backdropClick') {
            setOpenCreateModal(true)
        }
        else {
            setOpenCreateModal(false);
        }
    };

    const handleCloseSetupModal = (event, reason) => {
        if (reason == 'backdropClick') {
            setOpenSetupModal(true)
        }
        else {
            setOpenSetupModal(false);
        }
    };

    // Handle category changes
    const handleCategoryChange = (category) => {
        setActiveCategory(category);
    };

    // Handle search input changes
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle role filter changes
    const handleRoleFilterChange = (event) => {
        setSelectedRoleFilter(event.target.value);
    };

    // Handle status filter changes
    const handleStatusFilterChange = (event) => {
        setSelectedStatusFilter(event.target.value);
    };

    // Handle year selection for trends
    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
    };

    // Toggle filters visibility
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Generate data for the trends chart based on selected year
    const generateYearlyData = (year) => {
        // Check if we have real data for the selected year
        const activeUsersForYear = users.filter(user => {
            // Consider all users that were active during the selected year
            const dateGranted = user.DATE_GRANTED ? new Date(user.DATE_GRANTED) : null;
            // Filter out invalid dates (year < 1900)
            if (!dateGranted || dateGranted.getFullYear() < 1900) return false;
            
            const userYear = dateGranted.getFullYear();
            return userYear === year && user.STATUS && user.STATUS.toLowerCase() === 'active';
        });

        const newAssignmentsForYear = users.filter(user => {
            const dateGranted = user.DATE_GRANTED ? new Date(user.DATE_GRANTED) : null;
            // Filter out invalid dates (year < 1900)
            if (!dateGranted || dateGranted.getFullYear() < 1900) return false;
            
            return dateGranted.getFullYear() === year;
        });

        const termedAccessForYear = users.filter(user => {
            const dateRevoked = user.DATE_REVOKED ? new Date(user.DATE_REVOKED) : null;
            // Filter out invalid dates (year < 1900)
            if (!dateRevoked || dateRevoked.getFullYear() < 1900) return false;
            
            return dateRevoked.getFullYear() === year && user.STATUS && user.STATUS.toUpperCase() === 'INACTIVE';
        });

        // If there's no data for this year, return empty or null values
        if (activeUsersForYear.length === 0 && newAssignmentsForYear.length === 0 && termedAccessForYear.length === 0) {
            return {
                isEmpty: true,
                activeUsers: Array(12).fill(0),
                newAssignments: Array(12).fill(0),
                termedAccess: Array(12).fill(0)
            };
        }

        // Group users by month
        const activeUsersByMonth = Array(12).fill(0);
        const newAssignmentsByMonth = Array(12).fill(0);
        const termedAccessByMonth = Array(12).fill(0);

        // Count active users by month
        activeUsersForYear.forEach(user => {
            if (user.DATE_GRANTED) {
                const month = new Date(user.DATE_GRANTED).getMonth();
                activeUsersByMonth[month]++;
            }
        });

        // Count new assignments by month
        newAssignmentsForYear.forEach(user => {
            if (user.DATE_GRANTED) {
                const month = new Date(user.DATE_GRANTED).getMonth();
                newAssignmentsByMonth[month]++;
            }
        });

        // Count termed access by month
        termedAccessForYear.forEach(user => {
            if (user.DATE_REVOKED) {
                const month = new Date(user.DATE_REVOKED).getMonth();
                termedAccessByMonth[month]++;
            }
        });

        // Calculate cumulative active users (total active users per month)
        let cumulativeActiveUsers = Array(12).fill(0);
        let cumulativeCount = 0;
        for (let i = 0; i < 12; i++) {
            cumulativeCount += activeUsersByMonth[i];
            // Subtract terminated users for this month
            cumulativeCount -= termedAccessByMonth[i];
            cumulativeActiveUsers[i] = Math.max(0, cumulativeCount); // Ensure it doesn't go negative
        }

        return {
            isEmpty: false,
            activeUsers: cumulativeActiveUsers,
            newAssignments: newAssignmentsByMonth,
            termedAccess: termedAccessByMonth
        };
    };

    // After getting chart data based on selected year, add code to get available years with data
    const chartData = generateYearlyData(selectedYear);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Get years that have actual data
    const getAvailableYears = () => {
        // Get all possible years from data, filtering out invalid years (before 1900)
        const grantedYears = users
            .filter(user => user.DATE_GRANTED)
            .map(user => new Date(user.DATE_GRANTED).getFullYear())
            .filter(year => year >= 1900); // Filter out years before 1900
        
        const revokedYears = users
            .filter(user => user.DATE_REVOKED)
            .map(user => new Date(user.DATE_REVOKED).getFullYear())
            .filter(year => year >= 1900); // Filter out years before 1900
        
        // Combine all years and remove duplicates
        const allYears = [...new Set([...grantedYears, ...revokedYears])];
        
        // Sort years in descending order (newest first)
        return allYears.sort((a, b) => b - a);
    };

    const availableYears = getAvailableYears();

    // Set default selected year to the most recent year with data or current year if no data
    useEffect(() => {
        if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
            setSelectedYear(availableYears[0]);
        }
    }, [availableYears.length]);

    // Apply filters to the user data
    const applyFilters = () => {
        let filtered = [];
        
        // Choose which dataset to filter based on active category
        if (activeCategory === 'active') {
            filtered = [...allusers_rows];
        } else if (activeCategory === 'new') {
            filtered = [...newusers_rows];
        } else if (activeCategory === 'termed') {
            filtered = [...termedusers_rows];
        } else if (activeCategory === 'admin') {
            filtered = [...adminusers_rows];
        }

        // Apply role filter
        if (selectedRoleFilter !== 'All') {
            filtered = filtered.filter(user => user.ROLE_NAME === selectedRoleFilter);
        }

        // Apply status filter
        if (selectedStatusFilter !== 'All') {
            filtered = filtered.filter(user => 
                user.STATUS && user.STATUS.toLowerCase() === selectedStatusFilter.toLowerCase()
            );
        }

        // Apply search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(user => 
                (user.USER_ID && user.USER_ID.toLowerCase().includes(searchLower)) ||
                (user.EMAIL_ADDRESS && user.EMAIL_ADDRESS.toLowerCase().includes(searchLower)) ||
                (user.FIRST_NAME && user.FIRST_NAME.toLowerCase().includes(searchLower)) ||
                (user.LAST_NAME && user.LAST_NAME.toLowerCase().includes(searchLower)) ||
                (user.ROLE_NAME && user.ROLE_NAME.toLowerCase().includes(searchLower))
            );
        }

        setFilteredUsers(filtered);
    };

    // Reset filters
    const resetFilters = () => {
        setSelectedRoleFilter('All');
        setSelectedStatusFilter('All');
        setSearchTerm('');
        setFilteredUsers([]);
    };

    // Apply filters whenever filter parameters change
    useEffect(() => {
        applyFilters();
    }, [activeCategory, searchTerm]);

    //Get the active users
    const activeUsers = users.filter(user => user.STATUS.toLowerCase() === 'active');
    const totalActiveUsers = activeUsers.length;

    //Get unique users based on USER_ID
    const uniqueUserIds = Array.from(new Set(activeUsers.map(user => user.USER_ID)));
    const uniqueActiveUsers = uniqueUserIds.map(id => activeUsers.find(user => user.USER_ID === id));
    const totalUniqueActiveUsers = uniqueActiveUsers.length;

    const allusers_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'STATUS', headerName: 'Status', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
    ];

    const allusers_rows = activeUsers.map((user, index) => ({
        id: index + 1,
        USER_ID: user.USER_ID || '-',
        EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
        FIRST_NAME: user.FIRST_NAME || '-',
        LAST_NAME: user.LAST_NAME || '-',
        STATUS: user.STATUS || '-',
        ROLE_NAME: user.ROLE_NAME || '-',
        appID: user.id
    }));

    const currentYear = new Date().getFullYear();

    const newusers_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'STATUS', headerName: 'Status', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_GRANTED', headerName: 'Date Granted', flex: 1 },
    ];


    console.log('These are the users', users)
    const newusers_rows = users

        .filter(user => {
            const dateGrantedYear = new Date(user.DATE_GRANTED).getFullYear();
            return dateGrantedYear === currentYear;
        })
        .map((user, index) => ({
            id: index + 1,
            USER_ID: user.USER_ID || '-',
            EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
            FIRST_NAME: user.FIRST_NAME || '-',
            LAST_NAME: user.LAST_NAME || '-',
            STATUS: user.STATUS || '-',
            ROLE_NAME: user.ROLE_NAME || '-',
            DATE_GRANTED: user.DATE_GRANTED || '-',
            appID: user.id, 
        }));

    const totalNewUsers = newusers_rows.length


    const inactiveUsers = users.filter(user => user.STATUS.toLowerCase() === 'inactive');
    // Count the number of active users
   

    const termedusers_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'STATUS', headerName: 'Status', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_REVOKED', headerName: 'Date Termed', flex: 1 },
    ];

    const termedusers_rows = users
        .filter(user => {
            // Extract the year from DATE_REVOKED (assuming DATE_REVOKED is a valid date string)
            const dateRevokedYear = new Date(user.DATE_REVOKED).getFullYear();
            // Return true if the DATE_REVOKED is in the current year and STATUS is 'INACTIVE'
            return (dateRevokedYear === currentYear) &&
                (user.STATUS && user.STATUS.toUpperCase() === 'INACTIVE');
        })
        .map((user, index) => ({
            id: index + 1,
            USER_ID: user.USER_ID || '-',
            EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
            FIRST_NAME: user.FIRST_NAME || '-',
            LAST_NAME: user.LAST_NAME || '-',
            STATUS: user.STATUS || '-',
            ROLE_NAME: user.ROLE_NAME || '-',
            DATE_REVOKED: user.DATE_REVOKED || '-',
            appID: user.id
        }));

     const totalinactiveUsers = termedusers_rows.length;

    const adminusers_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'STATUS', headerName: 'Status', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_GRANTED', headerName: 'Date Granted', flex: 1 },
    ];

    const adminusers_rows = users
        .filter(user => {
            return (user.ROLE_NAME && user.ROLE_NAME.toLowerCase().includes('admin')) &&
                (user.STATUS && user.STATUS.toLowerCase() === 'active');
        })
        .map((user, index) => ({
            id: index + 1,
            USER_ID: user.USER_ID || '-',
            EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
            FIRST_NAME: user.FIRST_NAME || '-',
            LAST_NAME: user.LAST_NAME || '-',
            STATUS: user.STATUS || '-',
            ROLE_NAME: user.ROLE_NAME || '-',
            DATE_GRANTED: user.DATE_GRANTED || '-',
            appID: user.id
        }));

    // HR Mapping columns and rows
    const mapped_hr_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'App Role', flex: 1 },
        { field: 'jobTitle', headerName: 'Job Title', flex: 1 },
        { field: 'department', headerName: 'Department', flex: 1 },
        { field: 'manager', headerName: 'Manager', flex: 1 },
        { field: 'empType', headerName: 'Employee Type', flex: 1 },
        { field: 'hrStatus', headerName: 'HR Status', flex: 1 },
    ];

    const mapped_hr_rows = mappedUsers.map((user, index) => ({
        id: index + 1,
        USER_ID: user.USER_ID || '-',
        EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
        ROLE_NAME: user.ROLE_NAME || '-',
        jobTitle: user.hrInfo?.jobTitle || '-',
        department: user.hrInfo?.department || '-',
        manager: user.hrInfo?.manager || '-',
        empType: user.hrInfo?.empType || '-',
        hrStatus: user.hrInfo?.hrStatus || '-',
        appID: user.id
    }));

    const unmapped_hr_columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'USER_ID', headerName: 'ID', flex: 1 },
        { field: 'EMAIL_ADDRESS', headerName: 'Email Address', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'STATUS', headerName: 'Status', flex: 1 },
        { field: 'hrStatus', headerName: 'HR Status', flex: 1 },
    ];

    const unmapped_hr_rows = unmappedUsers.map((user, index) => ({
        id: index + 1,
        USER_ID: user.USER_ID || '-',
        EMAIL_ADDRESS: user.EMAIL_ADDRESS || '-',
        FIRST_NAME: user.FIRST_NAME || '-',
        LAST_NAME: user.LAST_NAME || '-',
        STATUS: user.STATUS || '-',
        ROLE_NAME: user.ROLE_NAME || '-',
        hrStatus: user.hrStatus || 'Not Mapped',
        appID: user.id
    }));

    const mapped_hr_columnsWithActions = [
        ...mapped_hr_columns,
        {
            field: 'view',
            headerName: 'Details',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title="View HR Details" arrow>
                    <mui.IconButton
                        sx={{ 
                            color: theme.palette.info.main,
                            backgroundColor: 'rgba(33, 150, 243, 0.08)',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.16)'
                            },
                            padding: '8px'
                        }}
                        size="small"
                        onClick={(event) => handleClick(event, params.row.appID)}
                    >
                        <PeopleIcon fontSize="small" />
                    </mui.IconButton>
                </Tooltip>
            ),
        },
    ];

    const unmapped_hr_columnsWithActions = [
        ...unmapped_hr_columns,
        {
            field: 'action',
            headerName: 'Action',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title="Map to HR" arrow>
                    <mui.IconButton
                        sx={{ 
                            color: theme.palette.warning.main,
                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 152, 0, 0.16)'
                            },
                            padding: '8px'
                        }}
                        size="small"
                        onClick={(event) => handleClick(event, params.row.appID)}
                    >
                        <LinkOffIcon fontSize="small" />
                    </mui.IconButton>
                </Tooltip>
            ),
        },
    ];

    const theme = createTheme({
        palette: {
            primary: {
                main: '#1976d2', // Blue
                light: '#42a5f5',
                dark: '#0d47a1',
                contrastText: '#fff',
            },
            secondary: {
                main: '#4caf50', // Green
                light: '#81c784',
                dark: '#388e3c',
                contrastText: '#fff',
            },
            error: {
                main: '#f44336',
                light: '#e57373',
                dark: '#d32f2f',
            },
            warning: {
                main: '#ff9800',
                light: '#ffb74d',
                dark: '#f57c00',
            },
            info: {
                main: '#2196f3',
                light: '#64b5f6',
                dark: '#1976d2',
            },
            success: {
                main: '#4caf50',
                light: '#81c784',
                dark: '#388e3c',
            },
            background: {
                default: '#f5f7fa',
                paper: '#ffffff',
            },
            text: {
                primary: '#2c3e50',
                secondary: '#7f8c8d',
            },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h4: {
                fontWeight: 700,
            },
            h5: {
                fontWeight: 600,
            },
            h6: {
                fontWeight: 600,
            },
            subtitle1: {
                fontWeight: 500,
            },
            subtitle2: {
                fontWeight: 500,
            },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        },
                    },
                    containedPrimary: {
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                    },
                },
            },
        },
    });


        const renderNewUserPrepButton = (params) => {
            return (
                <Tooltip title="View Approval Details" arrow>
                    <mui.IconButton
                        sx={{ 
                            color: theme.palette.primary.main,
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.16)'
                            },
                            padding: '8px'
                        }}
                        size="small"
                        onClick={(event) => handleClick(event, params.row.appID)}
                    >
                        <MarkEmailReadIcon fontSize="small" />
                    </mui.IconButton>
                </Tooltip>
            )
        }

    const renderTermedUserPrepButton = (params) => {
        return (
            <Tooltip title="View Termination Support" arrow>
                <mui.IconButton
                    sx={{ 
                        color: theme.palette.error.main,
                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.16)'
                        },
                        padding: '8px'
                    }}
                    size="small"
                    onClick={(event) => handleClick(event, params.row.appID)}
                >
                    <FolderDeleteIcon fontSize="small" />
                </mui.IconButton>
            </Tooltip>
        )
    }

    const renderAdminUserPrepButton = (params) => {
        return (
            <Tooltip title="View Users" arrow>
                <mui.IconButton
                    sx={{ 
                        color: theme.palette.primary.dark,
                        backgroundColor: 'rgba(13, 71, 161, 0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(13, 71, 161, 0.16)'
                        },
                        padding: '8px'
                    }}
                    size="small"
                    onClick={(event) => handleClick(event, params.row.appID)}
                >
                    <AdminPanelSettingsIcon fontSize="small" />
                </mui.IconButton>
            </Tooltip>
        )
    }

    const allusers_columnsWithActions = [
        ...allusers_columns,
    ];

    const newusers_columnsWithActions = [
        ...newusers_columns,
        {
            field: 'status',
            headerName: 'Support',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: renderNewUserPrepButton,
        },
    ];

    const termedusers_columnsWithActions = [
        ...termedusers_columns,
        {
            field: 'status',
            headerName: 'Support',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: renderTermedUserPrepButton,
        },
    ];

    const adminusers_columnsWithActions = [
        ...adminusers_columns,
        {
            field: 'status',
            headerName: 'User Details',
            width: 120,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: renderAdminUserPrepButton,
        },
    ];

    const saveAppChanges = async (updatedData) => {
        try {
            const response = await appService.updateApp(id, updatedData);
        } catch (error) {
            console.error('Error saving changes:', error);
        }
    };

    //GENERATE THE DATA FROM THE SERVER
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsDataLoading(true);
                console.log('Fetching application data...');
                
                // Fetch application data
                const appsByIdResponse = await appService.fetchAppsById(id);
                if (appsByIdResponse && appsByIdResponse.length !== 0) {
                    setSelectedApps(appsByIdResponse);
                } else {
                    setSelectedApps([]);
                }

                // Fetch user records for this application
                console.log('Fetching user records for application:', id);
                const result = await appService.fetchAppsRecordById(id);
                console.log(`Retrieved ${result.length} user records`);
                
                if (result.length !== 0) {
                    setUsers(result);
                    const updatedData = {
                        ...selectedapps,
                        SETUP_USERDATA: true
                    };
                    saveAppChanges(updatedData);
                    
                    // Fetch HR records for mapping
                    console.log('Fetching HR records for mapping...');
                    try {
                        // Use fetchActiveEmployees instead of fetchAllHRRecords
                        const hrData = await HRService.fetchActiveEmployees();
                        console.log('Active HR employees response:', hrData);
                        
                        // Ensure hrData is an array
                        const validHrData = Array.isArray(hrData) ? hrData : [];
                        console.log(`Retrieved ${validHrData.length} active HR records`);
                        
                        setHrRecords(validHrData);
                        
                        if (validHrData.length > 0) {
                            // Process HR mapping with valid data
                            console.log('Starting HR mapping process...');
                            processHrMapping(result, validHrData);
                        } else {
                            console.warn('No active HR records found for mapping');
                            // Set all users as unmapped since there are no HR records
                            setUnmappedUsers(result.map(user => ({...user, hrStatus: 'Not Mapped'})));
                            setMappedUsers([]);
                        }
                    } catch (hrError) {
                        console.error('Error fetching HR records:', hrError);
                        // Handle the error by setting all users as unmapped
                        setHrRecords([]);
                        setUnmappedUsers(result.map(user => ({...user, hrStatus: 'Not Mapped'})));
                        setMappedUsers([]);
                    }
                } else {
                    const updatedData = {
                        ...selectedapps,
                        SETUP_USERDATA: false
                    };
                    saveAppChanges(updatedData);
                    setOpenCreateModal(true);
                }

                const lastRefreshDate = await SFTPService.fetchAppDataImportLog(id);
          
                if (lastRefreshDate.length !== 0) {
                    setLastRefresh(lastRefreshDate);
                } else {
                    setLastRefresh([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsDataLoading(false);
            }
        };

        fetchData();
    }, [id, refreshTrigger]);

    // Process HR mapping
    const processHrMapping = (appUsers, hrUsers) => {
        // Initialize arrays for mapped and unmapped users
        const mapped = [];
        const unmapped = [];

        console.group('HR Mapping Process');
        console.log('Processing HR mapping...', {
            appUsersCount: appUsers.length,
            hrUsersCount: hrUsers.length
        });

        // Debug: Log full HR record structure to identify the correct email field
        if (hrUsers.length > 0) {
            console.log('First HR record full structure:', hrUsers[0]);
            console.log('Available fields in HR record:', Object.keys(hrUsers[0] || {}));
        }

        appUsers.forEach(user => {
            if (!user.EMAIL_ADDRESS) {
                console.log('App user missing email, adding to unmapped:', user.USER_ID);
                unmapped.push({...user, hrStatus: 'Not Mapped'});
                return;
            }

            // Normalize email for comparison
            const normalizedUserEmail = user.EMAIL_ADDRESS.toLowerCase().trim();
            console.log(`Looking for HR match for ${user.USER_ID} with email: "${normalizedUserEmail}"`);
            
            // Find matching HR record using the value field (which contains the email)
            const hrMatch = hrUsers.find(hr => {
                if (!hr.value) return false;
                // Normalize HR email for comparison
                const normalizedHrEmail = hr.value.toLowerCase().trim();
                const isMatch = normalizedHrEmail === normalizedUserEmail;
                if (isMatch) {
                    console.log(`Found match! HR email: "${normalizedHrEmail}" matches user email: "${normalizedUserEmail}"`);
                }
                return isMatch;
            });

            if (hrMatch) {
                console.log(`✅ Successfully mapped user ${user.USER_ID} to HR record with email ${hrMatch.value}`);
                
                // Extract name and role from the label field
                const labelParts = hrMatch.label.split('(');
                const name = labelParts[0].trim();
                const role = labelParts[1] ? labelParts[1].replace(')', '').trim() : '-';
                
                mapped.push({
                    ...user, 
                    hrStatus: 'Mapped',
                    hrInfo: {
                        jobTitle: role,
                        department: '-', // These fields aren't available in the current HR data
                        manager: '-',
                        hireDate: '-',
                        empType: '-',
                        hrStatus: 'Active' // Assuming active since these are from fetchActiveEmployees
                    }
                });
            } else {
                console.log(`❌ Could not find HR match for user ${user.USER_ID} with email ${user.EMAIL_ADDRESS}`);
                
                // Check for similar (but not exact) matches
                const similarMatches = hrUsers.filter(hr => {
                    if (!hr.value) return false;
                    const normalizedHrEmail = hr.value.toLowerCase().trim();
                    // Look for partial matches
                    return normalizedHrEmail.includes(normalizedUserEmail) || 
                           normalizedUserEmail.includes(normalizedHrEmail);
                });
                
                if (similarMatches.length > 0) {
                    console.log('Found similar (but not exact) matches:', 
                        similarMatches.map(hr => hr.value)
                    );
                }
                
                unmapped.push({...user, hrStatus: 'Not Mapped'});
            }
        });

        console.log('HR mapping complete', {
            totalUsers: appUsers.length,
            mappedCount: mapped.length,
            unmappedCount: unmapped.length
        });
        console.groupEnd();

        setMappedUsers(mapped);
        setUnmappedUsers(unmapped);
    };

    // Handle HR mapping category change
    const handleHrMappingCategoryChange = (category) => {
        setHrMappingCategory(category);
    };

    // Function to test and debug HR mapping issues
    const testHrMapping = () => {
        console.group('HR Mapping Test');
        
        // Check if we have both app users and HR records
        console.log('App users count:', users.length);
        console.log('HR records count:', hrRecords.length);
        
        if (users.length === 0 || hrRecords.length === 0) {
            console.warn('Missing data for proper HR mapping');
            console.groupEnd();
            return;
        }
        
        // Test specific email addresses that should match
        const testEmails = users.filter(u => u.EMAIL_ADDRESS).map(u => u.EMAIL_ADDRESS.toLowerCase().trim());
        const hrEmails = hrRecords.filter(h => h.EMAIL_ADDRESS).map(h => h.EMAIL_ADDRESS.toLowerCase().trim());
        
        console.log('Sample app user emails:', testEmails.slice(0, 5));
        console.log('Sample HR record emails:', hrEmails.slice(0, 5));
        
        // Find common emails
        const commonEmails = testEmails.filter(email => hrEmails.includes(email));
        console.log('Common emails found:', commonEmails.length);
        if (commonEmails.length > 0) {
            console.log('Example matches:', commonEmails.slice(0, 5));
        }
        
        // Find emails that should match but don't
        const unmatchedAppUsers = users.filter(user => {
            if (!user.EMAIL_ADDRESS) return false;
            const userEmail = user.EMAIL_ADDRESS.toLowerCase().trim();
            return !hrEmails.includes(userEmail);
        });
        
        console.log('Unmatched app users:', unmatchedAppUsers.length);
        if (unmatchedAppUsers.length > 0 && unmatchedAppUsers.length <= 10) {
            console.log('Unmatched app users details:', 
                unmatchedAppUsers.map(u => ({ 
                    userId: u.USER_ID, 
                    email: u.EMAIL_ADDRESS 
                }))
            );
        } else if (unmatchedAppUsers.length > 0) {
            console.log('First 5 unmatched app users:', 
                unmatchedAppUsers.slice(0, 5).map(u => ({ 
                    userId: u.USER_ID, 
                    email: u.EMAIL_ADDRESS 
                }))
            );
        }
        
        // Rerun HR mapping with the current data
        processHrMapping(users, hrRecords);
        
        console.groupEnd();
    };
    
    // Run the test after initial data load
    useEffect(() => {
        if (users && users.length > 0 && hrRecords && hrRecords.length > 0) {
            // Small delay to ensure state is updated
            const timer = setTimeout(() => {
                testHrMapping();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [users, hrRecords]);

    const formatDate = (dateString) => {
        if (!dateString) return ''; // Handle case where dateString is not available

        const date = new Date(dateString);
        return format(date, 'MMMM dd, yyyy'); // Format date using date-fns
    };

    //DONUT FOR ALL USERS
    const uniqueRoles = [...new Set(activeUsers.map(user => user.ROLE_NAME))];

    const roleCounts = {};
    activeUsers.forEach(user => {
        const roleName = user.ROLE_NAME;
        if (roleCounts[roleName]) {
            roleCounts[roleName]++;
        } else {
            roleCounts[roleName] = 1;
        }
    });

    const allusers = uniqueRoles.map(roleName => ({
        label: roleName,
        value: roleCounts[roleName] || 0
    }));

    const series = [
        {
            innerRadius: 0,
            outerRadius: 80,
            id: 'series-1',
            data: allusers,
        },
    ];

    //DONUT FOR NEW USERS
    const uniqueNewRoles = [...new Set(newusers_rows.map(user => user.ROLE_NAME))];

    const newroleCounts = {};
    
    newusers_rows.forEach(user => {
        const newroleName = user.ROLE_NAME;
        if (newroleCounts[newroleName]) {
            newroleCounts[newroleName]++;
        } else {
            newroleCounts[newroleName] = 1;
        }
    });

    const newusers = uniqueNewRoles.map(roleName => ({
        label: roleName,
        value: newroleCounts[roleName] || 0
    }));

    const newseries = [
        {
            innerRadius: 0,
            outerRadius: 80,
            id: 'series-1',
            data: newusers,
        },
    ];

      //DONUT FOR TERMED USERS
      const uniqueTermedRoles = [...new Set(termedusers_rows.map(user => user.ROLE_NAME))];

      const termedroleCounts = {};
      termedusers_rows.forEach(user => {
          const termedroleName = user.ROLE_NAME;
          if (termedroleCounts[termedroleName]) {
            termedroleCounts[termedroleName]++;
          } else {
            termedroleCounts[termedroleName] = 1;
          }
      });
  
      const termedusers = uniqueTermedRoles.map(roleName => ({
          label: roleName,
          value: termedroleCounts[roleName] || 0
      }));
  
      const termedseries = [
          {
              innerRadius: 0,
              outerRadius: 80,
              id: 'series-1',
              data: termedusers,
          },
      ];

    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

    const usersAddedByQuarter = {};

    //NEW ROLE ASSIGNMENT
    newusers_rows.forEach(user => {
        const dateGranted = new Date(user.DATE_GRANTED);
        const month = dateGranted.getMonth(); // Get the month (0-11)
    
        // Determine the quarter
        let quarter;
        if (month >= 0 && month <= 2) {
            quarter = 0; // Q1 (January to March)
        } else if (month >= 3 && month <= 5) {
            quarter = 1; // Q2 (April to June)
        } else if (month >= 6 && month <= 8) {
            quarter = 2; // Q3 (July to September)
        } else {
            quarter = 3; // Q4 (October to December)
        }
    
        if (!usersAddedByQuarter[quarter]) {
            usersAddedByQuarter[quarter] = 0;
        }
        usersAddedByQuarter[quarter]++;
    });
    
    const xAxisData = quarters.map((quarterLabel, index) => ({
        label: quarterLabel,
        value: usersAddedByQuarter[index] || 0
    }));
    
    const seriesData = [{
        data: xAxisData.map(quarter => quarter.value)
    }];

    const usersTermedByQuarter = {};

    //TERMED ROLE LIST
    termedusers_rows.forEach(user => {
        const dateRevoked = new Date(user.DATE_REVOKED);
        const month = dateRevoked.getMonth(); // Get the month (0-11)
    
        // Determine the quarter
        let quarter;
        if (month >= 0 && month <= 2) {
            quarter = 0; // Q1 (January to March)
        } else if (month >= 3 && month <= 5) {
            quarter = 1; // Q2 (April to June)
        } else if (month >= 6 && month <= 8) {
            quarter = 2; // Q3 (July to September)
        } else {
            quarter = 3; // Q4 (October to December)
        }
    
        if (!usersTermedByQuarter[quarter]) {
            usersTermedByQuarter[quarter] = 0;
        }
        usersTermedByQuarter[quarter]++;
    });
    
    const xAxisData_termed = quarters.map((quarterLabel, index) => ({
        label: quarterLabel,
        value: usersTermedByQuarter[index] || 0
    }));
    
    const seriesData_termed = [{
        data: xAxisData_termed.map(quarter => quarter.value)
    }];

    // Handle role selection for the chart
    const handleRoleSelectionForChart = (role) => {
        if (selectedRolesForChart.includes(role)) {
            setSelectedRolesForChart(selectedRolesForChart.filter(r => r !== role));
        } else {
            if (selectedRolesForChart.length < 8) {
                setSelectedRolesForChart([...selectedRolesForChart, role]);
            }
        }
    };

    // Get the top N roles by count
    const getTopRoles = (rolesData, count = 5) => {
        // Sort roles by count (value) in descending order
        return [...rolesData].sort((a, b) => b.value - a.value).slice(0, count);
    };

    // Prepare chart data based on user selection or top roles
    const prepareRoleChartData = () => {
        // If user has selected roles, filter to those roles
        if (selectedRolesForChart.length > 0) {
            return allusers.filter(role => selectedRolesForChart.includes(role.label));
        }
        
        // Otherwise return top 5 roles
        return getTopRoles(allusers);
    };

    // Get the filtered chart data
    const roleChartData = prepareRoleChartData();

    // Calculate "Others" category if showing top roles and there are more than 5 roles
    const calculateOthersCategory = () => {
        if (selectedRolesForChart.length > 0 || allusers.length <= 5) {
            return null;
        }

        const topRolesLabels = getTopRoles(allusers).map(role => role.label);
        const otherRoles = allusers.filter(role => !topRolesLabels.includes(role.label));
        
        if (otherRoles.length === 0) {
            return null;
        }

        const otherRolesTotal = otherRoles.reduce((sum, role) => sum + role.value, 0);
        return { label: 'Others', value: otherRolesTotal };
    };

    const othersCategory = calculateOthersCategory();

    // Final data to display in the chart
    const displayRoleChartData = othersCategory 
        ? [...roleChartData, othersCategory] 
        : roleChartData;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Add the bulk upload handlers here, after handleClose
    const handleOpenBulkUploadModal = () => {
        setIsBulkUploadOpen(true);
    };

    const handleCloseBulkUploadModal = () => {
        setIsBulkUploadOpen(false);
    };

    const handleBulkUploadSuccess = (results) => {
        // Determine the appropriate message based on results
        let message;
        if (results.created === 0 && results.updated === 0 && results.skipped > 0) {
            message = `All ${results.skipped} records are already up to date. No changes needed.`;
        } else {
            message = `Success! Created: ${results.created}, Updated: ${results.updated}, Skipped: ${results.skipped || 0} users`;
        }
        
        enqueueSnackbar(message, { 
            variant: results.created + results.updated > 0 ? 'success' : 'info',
            autoHideDuration: 5000,
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            }
        });
        
        handleCloseBulkUploadModal();
        
        // Update the last refresh date to now without waiting for the server
        const now = new Date();
        setLastRefresh({
            ...lastRefresh,
            JOB_DATE: now.toISOString()
        });
        
        // Refresh data by incrementing the trigger
        setRefreshTrigger(prev => prev + 1);
    };

    const customMainContent = (
        <ThemeProvider theme={theme}>
            <ResponsiveContainer sx={{ backgroundColor: theme.palette.background.default, padding: '24px' }}>
                {lastRefresh.JOB_DATE && (() => {
                    const lastRefreshDate = new Date(lastRefresh.JOB_DATE);
                    const currentDate = new Date();
                    const diffInMinutes = (currentDate - lastRefreshDate) / (1000 * 60);
                    const diffInDays = Math.round(diffInMinutes / 1440); // Convert minutes to days
                    
                    // Show warning if data is older than 3 days (4320 minutes)
                    if (diffInMinutes > 4320) {
                        return (
                            <mui.Alert 
                                severity="warning" 
                                sx={{ 
                                    mb: 2,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    border: '1px solid',
                                    borderColor: 'warning.light',
                                    backgroundColor: 'rgba(255, 152, 0, 0.04)',
                                    '& .MuiAlert-icon': { 
                                        fontSize: '1.25rem',
                                        color: 'warning.main'
                                    },
                                    '& .MuiAlert-message': { 
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        color: 'text.primary'
                                    }
                                }}
                                action={
                                    <mui.Button
                                        color="warning"
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            handleOpenBulkUploadModal();
                                            setRefreshTrigger(prev => prev + 1);
                                        }}
                                        startIcon={<RefreshIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 152, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        Refresh
                                    </mui.Button>
                                }
                            >
                                Your data is {diffInDays} {diffInDays === 1 ? 'day' : 'days'} old. Please refresh to see the latest information.
                            </mui.Alert>
                        );
                    }
                    return null;
                })()}
                <mui.Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <i className="material-icons" style={{ fontSize: '18px', marginRight: '4px' }}>home</i>
                        </mui.Box>
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        My Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/applications">
                        {selectedapps.COMPANY_NAME}
                    </mui.Link>
                    <mui.Typography color="text.primary">{selectedapps.APP_NAME}</mui.Typography>
                    <mui.Typography color="text.primary">Manage Users</mui.Typography>
                </mui.Breadcrumbs>

                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                    <GridViewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                    <mui.Box>
                        <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                            {selectedapps.APP_NAME} - User Management
                                </mui.Typography>
                        <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Manage, view access, and analyze user permissions across your application
                                    </mui.Typography>
                    </mui.Box>
                </mui.Box>

                <mui.Box sx={{ 
                    backgroundColor: '#f8fafc', 
                    borderRadius: 3,
                    padding: 3,
                    boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                }}>
                    <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                        <Tooltip title="Bulk Upload Users">
                            <mui.Button 
                                variant="contained"
                                color="primary"
                                startIcon={<UploadFileIcon />}
                                onClick={handleOpenBulkUploadModal}
                                sx={{ mr: 1 }}
                            >
                                Bulk Upload
                            </mui.Button>
                        </Tooltip>
                        <Tooltip title="Download Reports">
                            <mui.Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleClick}
                            >
                                Reports
                            </mui.Button>
                        </Tooltip>
                    </mui.Box>

                    <mui.Box className="stats-card" sx={{ mb: 4 }}>
                        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>Overview</mui.Typography>
                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary, mr: 1 }}>
                                    Last updated: {formatDate(lastRefresh.JOB_DATE) || 'N/A'}
                                </mui.Typography>
                                <Tooltip title="Refresh Data">
                                    <mui.IconButton size="small">
                                        <RefreshIcon fontSize="small" />
                                    </mui.IconButton>
                                </Tooltip>
                            </mui.Box>
                        </mui.Box>

                        <mui.Grid container spacing={3}>
                            <mui.Grid item xs={12} sm={6} md={3}>
                                <StatsCard className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <mui.Box>
                                            <StatsLabel>Active Users</StatsLabel>
                                            <StatsValue>{totalActiveUsers}</StatsValue>
                                        </mui.Box>
                                        <mui.Avatar sx={{ 
                                            bgcolor: 'rgba(25, 118, 210, 0.1)', 
                                            color: theme.palette.primary.main,
                                            width: 48,
                                            height: 48
                                        }}>
                                            <PeopleIcon />
                                        </mui.Avatar>
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                        <TrendBadge 
                                            label="18%" 
                                            size="small"
                                            icon={<TrendingUpIcon fontSize="small" />} 
                                            trend="up"
                                        />
                                        <mui.Typography variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                            vs last quarter
                                    </mui.Typography>
                                    </mui.Box>
                                </StatsCard>
                        </mui.Grid>
                            <mui.Grid item xs={12} sm={6} md={3}>
                                <StatsCard className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <mui.Box>
                                            <StatsLabel>Unique Users</StatsLabel>
                                            <StatsValue>{totalUniqueActiveUsers}</StatsValue>
                                        </mui.Box>
                                        <mui.Avatar sx={{ 
                                            bgcolor: 'rgba(76, 175, 80, 0.1)', 
                                            color: theme.palette.success.main,
                                            width: 48,
                                            height: 48
                                        }}>
                                            <PeopleIcon />
                                        </mui.Avatar>
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                        <TrendBadge 
                                            label="5%" 
                                            size="small"
                                            icon={<TrendingUpIcon fontSize="small" />} 
                                            trend="up"
                                        />
                                        <mui.Typography variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                            vs last quarter
                                        </mui.Typography>
                                    </mui.Box>
                                </StatsCard>
                    </mui.Grid>
                            <mui.Grid item xs={12} sm={6} md={3}>
                                <StatsCard className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <mui.Box>
                                            <StatsLabel>New Assignments</StatsLabel>
                                            <StatsValue color={theme.palette.success.main}>{totalNewUsers}</StatsValue>
                                        </mui.Box>
                                        <mui.Avatar sx={{ 
                                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                                            color: theme.palette.success.main,
                                            width: 48,
                                            height: 48
                                        }}>
                                            <PersonAdd />
                                        </mui.Avatar>
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                        <TrendBadge 
                                            label="12%" 
                                            size="small"
                                            icon={<TrendingUpIcon fontSize="small" />} 
                                            trend="up"
                                        />
                                        <mui.Typography variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                            vs last quarter
                                        </mui.Typography>
                                    </mui.Box>
                                </StatsCard>
                        </mui.Grid>
                            <mui.Grid item xs={12} sm={6} md={3}>
                                <StatsCard className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <mui.Box>
                                            <StatsLabel>Termed Access</StatsLabel>
                                            <StatsValue>{totalinactiveUsers}</StatsValue>
                                        </mui.Box>
                                        <mui.Avatar sx={{ 
                                            bgcolor: 'rgba(244, 67, 54, 0.1)', 
                                            color: theme.palette.error.main,
                                            width: 48,
                                            height: 48
                                        }}>
                                            <PersonOffIcon />
                                        </mui.Avatar>
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                        <TrendBadge 
                                            label="3%" 
                                            size="small"
                                            icon={<TrendingDownIcon fontSize="small" />} 
                                            trend="down"
                                        />
                                        <mui.Typography variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                            vs last quarter
                                        </mui.Typography>
                                    </mui.Box>
                                </StatsCard>
                        </mui.Grid>
                    </mui.Grid>
                    </mui.Box>

                    <mui.Box className="chart-container" sx={{ mb: 4 }}>
                        <mui.Grid container spacing={3}>
                            <mui.Grid item xs={12} md={8}>
                                <ChartContainer className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>User Activity Trends</mui.Typography>
                                        <mui.FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                                            <mui.InputLabel>Year</mui.InputLabel>
                                            <mui.Select
                                                value={selectedYear}
                                                onChange={handleYearChange}
                                                label="Year"
                                            >
                                                {availableYears.length > 0 ? (
                                                    availableYears.map(year => (
                                                        <mui.MenuItem key={year} value={year}>{year}</mui.MenuItem>
                                                    ))
                                                ) : (
                                                    <mui.MenuItem value={new Date().getFullYear()}>
                                                        {new Date().getFullYear()}
                                                    </mui.MenuItem>
                                                )}
                                            </mui.Select>
                                        </mui.FormControl>
                                    </mui.Box>
                                    <mui.Box sx={{ height: 300 }}>
                                        {chartData.isEmpty ? (
                                            <mui.Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                justifyContent: 'center', 
                                                alignItems: 'center', 
                                                height: '100%',
                                                color: theme.palette.text.secondary
                                            }}>
                                                <mui.Typography variant="h6" sx={{ mb: 1 }}>No Data Available</mui.Typography>
                                                <mui.Typography variant="body2">
                                                    There are no user records for {selectedYear}
                                                </mui.Typography>
                                            </mui.Box>
                                        ) : (
                                            <LineChart
                                                series={[
                                                    {
                                                        data: chartData.activeUsers,
                                                        label: 'Active Users',
                                                        color: theme.palette.primary.main,
                                                    },
                                                    {
                                                        data: chartData.newAssignments,
                                                        label: 'New Assignments',
                                                        color: theme.palette.success.main,
                                                    },
                                                    {
                                                        data: chartData.termedAccess,
            label: 'Termed Access',
                                                        color: theme.palette.error.main,
                                                    },
                                                ]}
                                                xAxis={[{ 
                                                    scaleType: 'point',
                                                    data: months,
                                                }]}
                                                height={300}
                                            />
                                        )}
                                    </mui.Box>
                                </ChartContainer>
                        </mui.Grid>
                            <mui.Grid item xs={12} md={4}>
                                <ChartContainer className="premium-card">
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>User Role Distribution</mui.Typography>
                                        <mui.Button 
                                            variant="outlined" 
                                            size="small"
                                            startIcon={<FilterListIcon />}
                                            onClick={() => setShowRoleSelector(!showRoleSelector)}
                                        >
                                            Filter Roles
                                        </mui.Button>
                                    </mui.Box>
                                    
                                    {showRoleSelector && (
                                        <mui.Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                                            <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Select up to 8 roles to display (currently showing {selectedRolesForChart.length || 'top 5'})
                                            </mui.Typography>
                                            <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '120px', overflowY: 'auto' }}>
                                                {allusers.map((role) => (
                                                    <mui.Chip
                                                        key={role.label}
                                                        label={`${role.label} (${role.value})`}
                                                        onClick={() => handleRoleSelectionForChart(role.label)}
                                                        color={selectedRolesForChart.includes(role.label) ? "primary" : "default"}
                                                        sx={{ m: 0.5 }}
                                                    />
                                                ))}
                                            </mui.Box>
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                <mui.Button 
                                                    size="small" 
                                                    onClick={() => setSelectedRolesForChart([])}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Reset to Top 5
                                                </mui.Button>
                                                <mui.Button 
                                                    size="small" 
                                                    variant="contained"
                                                    onClick={() => setShowRoleSelector(false)}
                                                >
                                                    Apply
                                                </mui.Button>
                                            </mui.Box>
                                        </mui.Box>
                                    )}
                                    
                                    <mui.Box sx={{ height: showRoleSelector ? 220 : 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {displayRoleChartData.length > 0 ? (
                                            <PieChart
                                                series={[
                                                    {
                                                        data: displayRoleChartData,
                                                        innerRadius: 60,
                                                        outerRadius: 100,
                                                        paddingAngle: 2,
                                                        cornerRadius: 4,
                                                        startAngle: -90,
                                                        endAngle: 270,
                                                        cx: 150,
                                                        cy: 120,
                                                    },
                                                ]}
                                                height={showRoleSelector ? 220 : 300}
                                                slotProps={{
                                                    legend: {
                                                        hidden: false,
                                                        position: { vertical: 'middle', horizontal: 'right' },
                                                        itemMarkWidth: 10,
                                                        itemMarkHeight: 10,
                                                        markGap: 5,
                                                        itemGap: 10,
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                                No role data available
                                            </mui.Typography>
                                        )}
                                    </mui.Box>
                                </ChartContainer>
                        </mui.Grid>
                        </mui.Grid>
                    </mui.Box>

                    <mui.Box className="data-section">
                        <SectionTitle variant="h6" sx={{ fontWeight: 600 }}>
                            <PeopleIcon /> User Management
                        </SectionTitle>

                        <mui.Box sx={{ mb: 3 }}>
                            <mui.Box sx={{ display: 'flex', mb: 2 }}>
                                <CategoryButton
                                    variant={activeCategory === 'active' ? 'contained' : 'outlined'}
                                    active={activeCategory === 'active'}
                                    onClick={() => handleCategoryChange('active')}
                                    startIcon={<PeopleIcon />}
                                >
                                    Active Users
                                </CategoryButton>
                                <CategoryButton
                                    variant={activeCategory === 'new' ? 'contained' : 'outlined'}
                                    active={activeCategory === 'new'}
                                    onClick={() => handleCategoryChange('new')}
                                    startIcon={<PersonAdd />}
                                >
                                    New Assignments
                                </CategoryButton>
                                <CategoryButton
                                    variant={activeCategory === 'termed' ? 'contained' : 'outlined'}
                                    active={activeCategory === 'termed'}
                                    onClick={() => handleCategoryChange('termed')}
                                    startIcon={<PersonOffIcon />}
                                >
                                    Termed Access
                                </CategoryButton>
                                <CategoryButton
                                    variant={activeCategory === 'admin' ? 'contained' : 'outlined'}
                                    active={activeCategory === 'admin'}
                                    onClick={() => handleCategoryChange('admin')}
                                    startIcon={<AdminPanelSettingsIcon />}
                                >
                                    Administrators
                                </CategoryButton>
                                <CategoryButton
                                    variant={activeCategory === 'hrMapping' ? 'contained' : 'outlined'}
                                    active={activeCategory === 'hrMapping'}
                                    onClick={() => handleCategoryChange('hrMapping')}
                                    startIcon={<FolderSharedIcon />}
                                >
                                    HR Mapping
                                </CategoryButton>
                            </mui.Box>

                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <mui.Box sx={{ display: 'flex', alignItems: 'center', width: '50%' }}>
                                    <mui.TextField
                                        variant="outlined"
                                        size="small"
                                        placeholder="Search users..."
                                        fullWidth
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        InputProps={{
                                            startAdornment: (
                                                <mui.InputAdornment position="start">
                                                    <SearchIcon />
                                                </mui.InputAdornment>
                                            ),
                                        }}
                                        sx={{ 
                                            mr: 1,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(25, 118, 210, 0.3)',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.primary.main,
                                                    borderWidth: '1px',
                                                }
                                            }
                                        }}
                                    />
                                    <mui.Button
                                        variant="outlined"
                                        startIcon={<FilterListIcon />}
                                        onClick={toggleFilters}
                                        size="medium"
                                        sx={{ 
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                        }}
                                    >
                                        Filters {showFilters ? '(active)' : ''}
                                    </mui.Button>
                                </mui.Box>
                            </mui.Box>

                            {showFilters && (
                                <mui.Box sx={{ 
                                    mb: 3, 
                                    p: 2, 
                                    bgcolor: '#f8fafc', 
                                    borderRadius: 2, 
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    border: '1px solid rgba(224, 224, 224, 0.4)',
                                    animation: 'fadeIn 0.3s ease-out'
                                }}>
                                    <mui.Grid container spacing={2} alignItems="center">
                                        <mui.Grid item xs={12} md={4}>
                                            <mui.FormControl fullWidth size="small">
                                                <mui.InputLabel>Role</mui.InputLabel>
                                                <mui.Select
                                                    value={selectedRoleFilter}
                                                    onChange={handleRoleFilterChange}
                                                    label="Role"
                                                    sx={{ 
                                                        borderRadius: '10px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(25, 118, 210, 0.3)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.primary.main,
                                                            borderWidth: '1px',
                                                        }
                                                    }}
                                                >
                                                    <mui.MenuItem value="All">All Roles</mui.MenuItem>
                                                    {uniqueRoles.map((role) => (
                                                        <mui.MenuItem key={role} value={role}>
                                                            {role}
                                                        </mui.MenuItem>
                                                    ))}
                                                </mui.Select>
                                            </mui.FormControl>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} md={4}>
                                            <mui.FormControl fullWidth size="small">
                                                <mui.InputLabel>Status</mui.InputLabel>
                                                <mui.Select
                                                    value={selectedStatusFilter}
                                                    onChange={handleStatusFilterChange}
                                                    label="Status"
                                                    sx={{ 
                                                        borderRadius: '10px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(25, 118, 210, 0.3)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.primary.main,
                                                            borderWidth: '1px',
                                                        }
                                                    }}
                                                >
                                                    <mui.MenuItem value="All">All Status</mui.MenuItem>
                                                    <mui.MenuItem value="Active">Active</mui.MenuItem>
                                                    <mui.MenuItem value="Inactive">Inactive</mui.MenuItem>
                                                </mui.Select>
                                            </mui.FormControl>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} md={4}>
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <mui.Button 
                                                    variant="outlined" 
                                                    sx={{ 
                                                        mr: 1,
                                                        borderRadius: '10px',
                                                        textTransform: 'none',
                                                        fontWeight: 500
                                                    }}
                                                    onClick={resetFilters}
                                                >
                                                    Reset
                                                </mui.Button>
                                                <mui.Button 
                                                    variant="contained"
                                                    onClick={applyFilters}
                                                    sx={{ 
                                                        borderRadius: '10px',
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        boxShadow: '0 2px 6px rgba(25, 118, 210, 0.2)'
                                                    }}
                                                >
                                                    Apply Filters
                                                </mui.Button>
                                            </mui.Box>
                                        </mui.Grid>
                                    </mui.Grid>
                                </mui.Box>
                            )}

                            <DataCard className="premium-card">
                                <Suspense fallback={
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <mui.CircularProgress 
                                            size={40} 
                                            thickness={4}
                                            sx={{
                                                color: 'primary.main',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                    </mui.Box>
                                }>
                                    {activeCategory === 'active' && (
                                        <DataTable
                                            rows={filteredUsers.length > 0 ? filteredUsers : allusers_rows}
                                            columns={allusers_columns}
                                            columnsWithActions={allusers_columnsWithActions}
                                            height={450}
                                        />
                                    )}
                                    {activeCategory === 'new' && (
                                        <DataTable
                                            rows={filteredUsers.length > 0 ? filteredUsers : newusers_rows}
                                            columns={newusers_columns}
                                            columnsWithActions={newusers_columnsWithActions}
                                            height={450}
                                        />
                                    )}
                                    {activeCategory === 'termed' && (
                                        <DataTable
                                            rows={filteredUsers.length > 0 ? filteredUsers : termedusers_rows}
                                            columns={termedusers_columns}
                                            columnsWithActions={termedusers_columnsWithActions}
                                            height={450}
                                        />
                                    )}
                                    {activeCategory === 'admin' && (
                                        <DataTable
                                            rows={filteredUsers.length > 0 ? filteredUsers : adminusers_rows}
                                            columns={adminusers_columns}
                                            columnsWithActions={adminusers_columnsWithActions}
                                            height={450}
                                        />
                                    )}
                                </Suspense>
                            </DataCard>
                        </mui.Box>

                        {activeCategory === 'active' && (
                            <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Active Role Distribution</mui.Typography>
                                        <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                            <PieChart
                                                series={[
                                                    {
                                                        data: allusers.length > 5 
                                                            ? [
                                                                ...allusers
                                                                  .sort((a, b) => b.value - a.value)
                                                                  .slice(0, 5),
                                                                {
                                                                  label: 'Others',
                                                                  value: allusers
                                                                    .sort((a, b) => b.value - a.value)
                                                                    .slice(5)
                                                                    .reduce((sum, role) => sum + role.value, 0)
                                                                }
                                                              ]
                                                            : allusers,
                                                        innerRadius: 60,
                                                        outerRadius: 100,
                                                        paddingAngle: 2,
                                                        cornerRadius: 4,
                                                    },
                                                ]}
                                                height={300}
                                                slotProps={{
                                                    legend: {
                                                        direction: 'column',
                                                        position: { vertical: 'middle', horizontal: 'right' },
                                                        padding: 0,
                                                        itemMarkWidth: 12,
                                                        itemMarkHeight: 12,
                                                        markGap: 5,
                                                        itemGap: 8,
                                                    },
                                                }}
                                            />
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>User Activity Insights</mui.Typography>
                                        <mui.Box sx={{ p: 2 }}>
                                            <mui.List>
                                                <mui.ListItem sx={{ borderRadius: 2, mb: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                    <mui.ListItemIcon>
                                                        <mui.Avatar sx={{ bgcolor: theme.palette.primary.light, width: 32, height: 32 }}>
                                                            <TrendingUpIcon fontSize="small" />
                                                        </mui.Avatar>
                                                    </mui.ListItemIcon>
                                                    <mui.ListItemText 
                                                        primary={
                                                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                Most Common Role
                    </mui.Typography>
                                                        }
                                                        secondary={
                                                            <mui.Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                                                {allusers.length > 0 
                                                                    ? allusers.sort((a, b) => b.value - a.value)[0].label 
                                                                    : 'N/A'}
                                                            </mui.Typography>
                                                        } 
                                                    />
                                                </mui.ListItem>
                                                <mui.Divider variant="inset" component="li" />
                                                <mui.ListItem sx={{ borderRadius: 2, my: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                    <mui.ListItemIcon>
                                                        <mui.Avatar sx={{ bgcolor: theme.palette.success.light, width: 32, height: 32 }}>
                                                            <PeopleIcon fontSize="small" />
                                                        </mui.Avatar>
                                                    </mui.ListItemIcon>
                                                    <mui.ListItemText 
                                                        primary={
                                                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                User Utilization
                                                            </mui.Typography>
                                                        }
                                                        secondary={
                                                            <mui.Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                                                                {`${totalUniqueActiveUsers} unique users out of ${totalActiveUsers} total roles`}
                                                            </mui.Typography>
                                                        } 
                                                    />
                                                </mui.ListItem>
                                                <mui.Divider variant="inset" component="li" />
                                                <mui.ListItem sx={{ borderRadius: 2, mt: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                    <mui.ListItemIcon>
                                                        <mui.Avatar sx={{ bgcolor: theme.palette.info.light, width: 32, height: 32 }}>
                                                            <RefreshIcon fontSize="small" />
                                                        </mui.Avatar>
                                                    </mui.ListItemIcon>
                                                    <mui.ListItemText 
                                                        primary={
                                                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                Last Data Refresh
                                                            </mui.Typography>
                                                        }
                                                        secondary={
                                                            <mui.Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 500 }}>
                                                                {formatDate(lastRefresh.JOB_DATE) || 'N/A'}
                                                            </mui.Typography>
                                                        } 
                                                    />
                                                </mui.ListItem>
                                            </mui.List>
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                            </mui.Grid>
                        )}

                        {activeCategory === 'new' && (
                            <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>New Role Assignments by Quarter</mui.Typography>
                                        <mui.Box sx={{ height: 300 }}>
                                            <BarChart
                                                xAxis={[{ scaleType: 'band', data: quarters }]}
                                                series={[{
                                                    data: xAxisData.map(quarter => quarter.value),
                                                    label: 'New Assignments',
                                                    color: theme.palette.success.main,
                                                }]}
                                                height={300}
                                            />
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>New Role Distribution</mui.Typography>
                                        <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                            <PieChart
                                                series={[
                                                    {
                                                        data: newusers.length > 5 
                                                            ? [
                                                                ...newusers
                                                                  .sort((a, b) => b.value - a.value)
                                                                  .slice(0, 5),
                                                                {
                                                                  label: 'Others',
                                                                  value: newusers
                                                                    .sort((a, b) => b.value - a.value)
                                                                    .slice(5)
                                                                    .reduce((sum, role) => sum + role.value, 0)
                                                                }
                                                              ]
                                                            : newusers,
                                                        innerRadius: 60,
                                                        outerRadius: 100,
                                                        paddingAngle: 2,
                                                        cornerRadius: 4,
                                                    },
                                                ]}
                                                height={300}
                                                width={400}
                                                slotProps={{
                                                    legend: {
                                                        direction: 'column',
                                                        position: { vertical: 'middle', horizontal: 'right' },
                                                        padding: 0,
                                                        itemMarkWidth: 12,
                                                        itemMarkHeight: 12,
                                                        markGap: 5,
                                                        itemGap: 8,
                                                    },
                                                }}
                                            />
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                            </mui.Grid>
                        )}

                        {activeCategory === 'termed' && (
                            <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Terminations by Quarter</mui.Typography>
                                        <mui.Box sx={{ height: 300 }}>
                                            <BarChart
                                                xAxis={[{ scaleType: 'band', data: quarters }]}
                                                series={[{
                                                    data: xAxisData_termed.map(quarter => quarter.value),
                                                    label: 'Terminations',
                                                    color: theme.palette.error.main,
                                                }]}
                                                height={300}
                                            />
                    </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                                <mui.Grid item xs={12} md={6}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PersonOffIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                                                Termed Role Distribution
                                            </mui.Box>
                                        </mui.Typography>
                                        <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                            {termedusers.length > 0 ? (
                                                <PieChart
                                                    series={[
                                                        {
                                                            data: termedusers.length > 5 
                                                                ? [
                                                                    ...termedusers
                                                                      .sort((a, b) => b.value - a.value)
                                                                      .slice(0, 5),
                                                                    {
                                                                      label: 'Others',
                                                                      value: termedusers
                                                                        .sort((a, b) => b.value - a.value)
                                                                        .slice(5)
                                                                        .reduce((sum, role) => sum + role.value, 0)
                                                                    }
                                                                  ]
                                                                : termedusers,
                                                            innerRadius: 60,
                                                            outerRadius: 100,
                                                            paddingAngle: 2,
                                                            cornerRadius: 4,
                                                            arcLabel: (item) => `${item.value}`,
                                                            arcLabelMinAngle: 20,
                                                        },
                                                    ]}
                                                    height={300}
                                                    width={400}
                                                    colors={['#f44336', '#e57373', '#ef5350', '#d32f2f', '#c62828', '#757575']}
                                                    slotProps={{
                                                        legend: {
                                                            direction: 'column',
                                                            position: { vertical: 'middle', horizontal: 'right' },
                                                            padding: 0,
                                                            itemMarkWidth: 12,
                                                            itemMarkHeight: 12,
                                                            markGap: 5,
                                                            itemGap: 8,
                                                        },
                                                    }}
                                                    margin={{ right: 120 }}
                                                    sx={{
                                                        [`& .${pieArcLabelClasses.root}`]: {
                                                            fill: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: 12,
                                                        },
                                                    }}
                                                />
                                            ) : (
                                                <mui.Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    height: '100%',
                                                    color: theme.palette.text.secondary
                                                }}>
                                                    <mui.Avatar sx={{ 
                                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                                        color: theme.palette.error.main,
                                                        width: 48,
                                                        height: 48,
                                                        mb: 2
                                                    }}>
                                                        <PersonOffIcon />
                                                    </mui.Avatar>
                                                    <mui.Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                        No termination data available
                                                    </mui.Typography>
                                                    <mui.Typography variant="body2" sx={{ mt: 1 }}>
                                                        There are no terminated users in the current year
                                                    </mui.Typography>
                                                </mui.Box>
                                            )}
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                            </mui.Grid>
                        )}

                        {activeCategory === 'admin' && (
                            <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                                <mui.Grid item xs={12}>
                                    <ChartContainer className="premium-card">
                                        <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <AdminPanelSettingsIcon sx={{ mr: 1, color: theme.palette.primary.dark }} />
                                                Administrator Distribution
                                            </mui.Box>
                                        </mui.Typography>
                                        <mui.Box sx={{ p: 2 }}>
                                            <mui.Typography variant="body1" sx={{ mb: 2 }}>
                                                This application has {adminusers_rows.length} administrator{adminusers_rows.length !== 1 ? 's' : ''}.
                                            </mui.Typography>
                                        </mui.Box>
                                    </ChartContainer>
                                </mui.Grid>
                            </mui.Grid>
                        )}

                        {activeCategory === 'hrMapping' && (
                            <>
                                <mui.Box sx={{ mb: 4 }}>
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PeopleIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                                HR Mapping Overview
                                            </mui.Box>
                                        </mui.Typography>
                                        
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {isDataLoading ? (
                                                <mui.Chip
                                                    icon={<RefreshIcon className="animate-spin" />}
                                                    label="Refreshing HR Data"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        mr: 1,
                                                        '& .animate-spin': {
                                                            animation: 'spin 2s linear infinite',
                                                            '@keyframes spin': {
                                                                '0%': { transform: 'rotate(0deg)' },
                                                                '100%': { transform: 'rotate(360deg)' }
                                                            }
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <mui.Button
                                                    variant="outlined"
                                                    startIcon={<RefreshIcon />}
                                                    size="small"
                                                    onClick={() => setRefreshTrigger(prev => prev + 1)}
                                                    sx={{ mr: 1, fontWeight: 500, textTransform: 'none' }}
                                                >
                                                    Refresh
                                                </mui.Button>
                                            )}
                                        </mui.Box>
                                    </mui.Box>

                                    <mui.Grid container spacing={3}>
                                        <mui.Grid item xs={12} md={4}>
                                            <StatsCard className="premium-card">
                                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <mui.Box>
                                                        <StatsLabel>Total Users</StatsLabel>
                                                        <StatsValue>{users.length}</StatsValue>
                                                    </mui.Box>
                                                    <mui.Avatar sx={{ 
                                                        bgcolor: 'rgba(33, 150, 243, 0.1)', 
                                                        color: theme.palette.info.main,
                                                        width: 48,
                                                        height: 48
                                                    }}>
                                                        <PeopleIcon />
                                                    </mui.Avatar>
                                                </mui.Box>
                                            </StatsCard>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} md={4}>
                                            <StatsCard className="premium-card">
                                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <mui.Box>
                                                        <StatsLabel>HR Mapped</StatsLabel>
                                                        <StatsValue color={theme.palette.success.main}>{mappedUsers.length}</StatsValue>
                                                    </mui.Box>
                                                    <mui.Avatar sx={{ 
                                                        bgcolor: 'rgba(76, 175, 80, 0.1)', 
                                                        color: theme.palette.success.main,
                                                        width: 48,
                                                        height: 48
                                                    }}>
                                                        <FolderSharedIcon />
                                                    </mui.Avatar>
                                                </mui.Box>
                                                <mui.Box sx={{ mt: 1 }}>
                                                    <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                        {mappedUsers.length > 0 ? 
                                                            `${Math.round((mappedUsers.length / users.length) * 100)}% of users mapped` : 
                                                            'No users mapped'
                                                        }
                                                    </mui.Typography>
                                                </mui.Box>
                                            </StatsCard>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} md={4}>
                                            <StatsCard className="premium-card">
                                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <mui.Box>
                                                        <StatsLabel>Not Mapped</StatsLabel>
                                                        <StatsValue color={theme.palette.warning.main}>{unmappedUsers.length}</StatsValue>
                                                    </mui.Box>
                                                    <mui.Avatar sx={{ 
                                                        bgcolor: 'rgba(255, 152, 0, 0.1)', 
                                                        color: theme.palette.warning.main,
                                                        width: 48,
                                                        height: 48
                                                    }}>
                                                        <LinkOffIcon />
                                                    </mui.Avatar>
                                                </mui.Box>
                                                <mui.Box sx={{ mt: 1 }}>
                                                    <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                        {unmappedUsers.length > 0 ? 
                                                            `${Math.round((unmappedUsers.length / users.length) * 100)}% of users not mapped` : 
                                                            'All users mapped'
                                                        }
                                                    </mui.Typography>
                                                </mui.Box>
                                            </StatsCard>
                                        </mui.Grid>
                                    </mui.Grid>
                                </mui.Box>

                                <mui.Box sx={{ mb: 3 }}>
                                    <mui.Box sx={{ display: 'flex', mb: 2 }}>
                                        <CategoryButton
                                            variant={hrMappingCategory === 'mapped' ? 'contained' : 'outlined'}
                                            active={hrMappingCategory === 'mapped'}
                                            onClick={() => handleHrMappingCategoryChange('mapped')}
                                            startIcon={<FolderSharedIcon />}
                                        >
                                            HR Mapped Users
                                        </CategoryButton>
                                        <CategoryButton
                                            variant={hrMappingCategory === 'unmapped' ? 'contained' : 'outlined'}
                                            active={hrMappingCategory === 'unmapped'}
                                            onClick={() => handleHrMappingCategoryChange('unmapped')}
                                            startIcon={<LinkOffIcon />}
                                        >
                                            Unmapped Users
                                        </CategoryButton>
                                    </mui.Box>

                                    <DataCard className="premium-card">
                                        <Suspense fallback={
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                <mui.CircularProgress 
                                                    size={40} 
                                                    thickness={4}
                                                    sx={{
                                                        color: 'primary.main',
                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                    }}
                                                />
                                            </mui.Box>
                                        }>
                                            {hrMappingCategory === 'mapped' && (
                                                <DataTable
                                                    rows={mapped_hr_rows}
                                                    columns={mapped_hr_columns}
                                                    columnsWithActions={mapped_hr_columnsWithActions}
                                                    height={450}
                                                />
                                            )}
                                            {hrMappingCategory === 'unmapped' && (
                                                <DataTable
                                                    rows={unmapped_hr_rows}
                                                    columns={unmapped_hr_columns}
                                                    columnsWithActions={unmapped_hr_columnsWithActions}
                                                    height={450}
                                                />
                                            )}
                                        </Suspense>
                                    </DataCard>
                                </mui.Box>

                                <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                                    <mui.Grid item xs={12} md={6}>
                                        <ChartContainer className="premium-card">
                                            <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <FolderSharedIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                                    HR Mapping Status
                                                </mui.Box>
                                            </mui.Typography>
                                            <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                                <PieChart
                                                    series={[
                                                        {
                                                            data: [
                                                                { label: 'Mapped', value: mappedUsers.length },
                                                                { label: 'Not Mapped', value: unmappedUsers.length }
                                                            ],
                                                            innerRadius: 60,
                                                            outerRadius: 100,
                                                            paddingAngle: 2,
                                                            cornerRadius: 4,
                                                            arcLabel: (item) => `${item.value}`,
                                                            arcLabelMinAngle: 20,
                                                        },
                                                    ]}
                                                    height={300}
                                                    colors={[theme.palette.success.main, theme.palette.warning.main]}
                                                    slotProps={{
                                                        legend: {
                                                            direction: 'column',
                                                            position: { vertical: 'middle', horizontal: 'right' },
                                                            padding: 0,
                                                            itemMarkWidth: 12,
                                                            itemMarkHeight: 12,
                                                            markGap: 5,
                                                            itemGap: 8,
                                                        },
                                                    }}
                                                    margin={{ right: 120 }}
                                                    sx={{
                                                        [`& .${pieArcLabelClasses.root}`]: {
                                                            fill: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: 12,
                                                        },
                                                    }}
                                                />
                                            </mui.Box>
                                        </ChartContainer>
                                    </mui.Grid>
                                    <mui.Grid item xs={12} md={6}>
                                        <ChartContainer className="premium-card">
                                            <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>HR Mapping Insights</mui.Typography>
                                            <mui.Box sx={{ p: 2 }}>
                                                <mui.List>
                                                    <mui.ListItem sx={{ borderRadius: 2, mb: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                        <mui.ListItemIcon>
                                                            <mui.Avatar sx={{ bgcolor: theme.palette.success.light, width: 32, height: 32 }}>
                                                                <FolderSharedIcon fontSize="small" />
                                                            </mui.Avatar>
                                                        </mui.ListItemIcon>
                                                        <mui.ListItemText 
                                                            primary={
                                                                <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    Mapping Status
                                                                </mui.Typography>
                                                            }
                                                            secondary={
                                                                <mui.Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 500 }}>
                                                                    {mappedUsers.length > 0 ? 
                                                                        `${Math.round((mappedUsers.length / users.length) * 100)}% of users have HR records mapped` : 
                                                                        'No users have HR records mapped'
                                                                    }
                                                                </mui.Typography>
                                                            } 
                                                        />
                                                    </mui.ListItem>
                                                    <mui.Divider variant="inset" component="li" />
                                                    <mui.ListItem sx={{ borderRadius: 2, my: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                        <mui.ListItemIcon>
                                                            <mui.Avatar sx={{ bgcolor: theme.palette.warning.light, width: 32, height: 32 }}>
                                                                <LinkOffIcon fontSize="small" />
                                                            </mui.Avatar>
                                                        </mui.ListItemIcon>
                                                        <mui.ListItemText 
                                                            primary={
                                                                <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    Unmapped Users
                                                                </mui.Typography>
                                                            }
                                                            secondary={
                                                                <mui.Typography variant="body2" sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                                                                    {unmappedUsers.length} users need HR mapping
                                                                </mui.Typography>
                                                            } 
                                                        />
                                                    </mui.ListItem>
                                                    <mui.Divider variant="inset" component="li" />
                                                    <mui.ListItem sx={{ borderRadius: 2, mt: 1, transition: 'background-color 0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                                        <mui.ListItemIcon>
                                                            <mui.Avatar sx={{ bgcolor: theme.palette.info.light, width: 32, height: 32 }}>
                                                                <RefreshIcon fontSize="small" />
                                                            </mui.Avatar>
                                                        </mui.ListItemIcon>
                                                        <mui.ListItemText 
                                                            primary={
                                                                <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    Last Data Refresh
                                                                </mui.Typography>
                                                            }
                                                            secondary={
                                                                <mui.Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 500 }}>
                                                                    {formatDate(lastRefresh.JOB_DATE) || 'N/A'}
                                                                </mui.Typography>
                                                            } 
                                                        />
                                                    </mui.ListItem>
                                                </mui.List>
                                            </mui.Box>
                                        </ChartContainer>
                                    </mui.Grid>
                                </mui.Grid>
                            </>
                        )}
                    </mui.Box>

                    <mui.Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <mui.MenuItem onClick={handleClose}>
                            <mui.ListItemIcon>
                                <FolderSharedIcon fontSize="small" />
                            </mui.ListItemIcon>
                            All Users Report
                        </mui.MenuItem>
                        <mui.MenuItem onClick={handleClose}>
                            <mui.ListItemIcon>
                                <CreateNewFolderIcon fontSize="small" />
                            </mui.ListItemIcon>
                            New Role Assignments
                        </mui.MenuItem>
                        <mui.Divider />
                        <mui.MenuItem onClick={handleClose}>
                            <mui.ListItemIcon>
                                <FolderDeleteIcon fontSize="small" />
                            </mui.ListItemIcon>
                            Termed Users
                        </mui.MenuItem>
                        <mui.MenuItem onClick={handleClose}>
                            <mui.ListItemIcon>
                                <AdminPanelSettingsIcon fontSize="small" />
                            </mui.ListItemIcon>
                            Administrators
                        </mui.MenuItem>
                        <mui.MenuItem onClick={handleClose}>
                            <mui.ListItemIcon>
                                <LinkOffIcon fontSize="small" />
                            </mui.ListItemIcon>
                            Unmapped users
                        </mui.MenuItem>
                    </mui.Menu>

                <Modal
                    open={openCreateModal}
                    onClose={handleCloseCreateModal}
                    header="No User Records Found"
                    body={
                        <>
                            <mui.Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                py: 2
                            }}>
                                <mui.Avatar sx={{ 
                                    bgcolor: 'rgba(25, 118, 210, 0.1)', 
                                    color: theme.palette.primary.main,
                                    width: 64,
                                    height: 64,
                                    mb: 2
                                }}>
                                    <PeopleIcon fontSize="large" />
                                </mui.Avatar>
                                
                                <mui.Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                    No User Records Found
                                </mui.Typography>
                                
                                <mui.Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 450 }}>
                                    No user records have been set up for {selectedapps.APP_NAME} yet. You can easily add users by using our bulk upload feature.
                                </mui.Typography>
                                
                                <mui.Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<UploadFileIcon />} 
                                    onClick={() => {
                                        handleCloseCreateModal();
                                        handleOpenBulkUploadModal();
                                    }}
                                    sx={{ 
                                        px: 3,
                                        py: 1,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontSize: '1rem'
                                    }}
                                >
                                    Start Bulk Upload
                                </mui.Button>
                            </mui.Box>
                        </>
                    }
                    footer={
                        <>
                            <mui.Button sx={{ marginBottom: '20px' }} onClick={handleCloseCreateModal} color="primary">
                                Close
                            </mui.Button>
                        </>
                    }
                />

                <Modal
                    open={openSetupModal}
                    onClose={handleCloseSetupModal}
                    header="Upload User Data"
                    size="lg"
                    body={
                        <>
                            <mui.Box>
                                <FileUploadUserBulk
                                    onSuccess={(results) => {
                                        handleBulkUploadSuccess(results);
                                        handleCloseSetupModal();
                                    }}
                                    appName={selectedapps?.APP_NAME}
                                />
                            </mui.Box>
                        </>
                    }
                />

                <mui.Dialog
                    open={isBulkUploadOpen}
                    onClose={handleCloseBulkUploadModal}
                    maxWidth="lg"
                    fullWidth
                    aria-labelledby="bulk-upload-dialog-title"
                >
                    <mui.DialogTitle id="bulk-upload-dialog-title" sx={{ pb: 1 }}>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            Bulk User Management
                            <mui.IconButton 
                                aria-label="close" 
                                onClick={handleCloseBulkUploadModal}
                                size="small"
                            >
                                <CloseIcon />
                            </mui.IconButton>
                        </mui.Box>
                    </mui.DialogTitle>
                    <mui.DialogContent dividers>
                        <FileUploadUserBulk
                            onSuccess={handleBulkUploadSuccess}
                            appName={selectedapps?.APP_NAME}
                            isDialog={true}
                        />
                    </mui.DialogContent>
                </mui.Dialog>
                </mui.Box>
            </ResponsiveContainer>
        </ThemeProvider>
    );

    return (
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />
        </div>
    );
}

export default MyApplicationManageUsers;